import { useCallback, useEffect, useState } from "react";
import type { PendingAction } from "@yagni/shared";
import type { EditableProposal } from "@yagni/shared";

/** Why an approved code publication did not complete: its code and any secret-scan kinds. */
export interface CodePublicationFailure {
  failureCode: string;
  scanFindingKinds: string[];
  scanFindingKindCount: number;
  /** Where the scan fired: each kind with its `path:line`, never the value. Absent from older servers. */
  scanFindingLocations?: Array<{ kind: string; location: string; shape?: string }>;
  /** Located findings past the server's cap. */
  scanFindingLocationsOmitted?: number;
  /** What happened and what to do next, for a person. Absent from older servers. */
  sentence?: string;
  /** Set while Wright waits to try a transient cause again on its own. */
  retrying?: { attempt: number; maxAttempts: number; nextAttemptAt: string } | null;
  /** A secret-scan stop a person may publish past: every hit is a random value next to a sensitive name. */
  publishAnyway?: boolean;
}

export interface OpenWorkItemPendingAction {
  action: PendingAction;
  proposal: EditableProposal | null;
  publicationFailure?: CodePublicationFailure | null;
}

/**
 * The work item's open LINKED pending_action — the "your turn" object for a
 * staged draft (work-item spine, YAG-111 §8). Mirrors useOpenWorkItemGate;
 * 404 means no open action (not an error).
 */
export function useOpenWorkItemPendingAction(workItemId: string | null): {
  open: OpenWorkItemPendingAction | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
} {
  const [open, setOpen] = useState<OpenWorkItemPendingAction | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const refetch = useCallback(async () => {
    if (!workItemId) {
      setOpen(null);
      setIsLoading(false);
      setError(null);
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const res = await fetch(`/api/work-items/${workItemId}/pending-action/open`, {
        credentials: "include",
      });
      if (res.status === 404) {
        setOpen(null);
        return;
      }
      if (!res.ok) {
        throw new Error(`Pending action load failed (${res.status})`);
      }
      const payload = (await res.json()) as {
        action?: PendingAction | null;
        proposal?: EditableProposal | null;
        publicationFailure?: CodePublicationFailure | null;
      };
      setOpen(payload.action
        ? { action: payload.action, proposal: payload.proposal ?? null, publicationFailure: payload.publicationFailure ?? null }
        : null);
    } catch (err) {
      setOpen(null);
      setError(err instanceof Error ? err : new Error("Pending action load failed"));
    } finally {
      setIsLoading(false);
    }
  }, [workItemId]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  return { open, isLoading, error, refetch };
}

export type ResolvePendingActionVerb = "approve" | "reject" | "retry" | "rebuild" | "publish-anyway";

export interface ResolvePendingActionResult {
  /** Approval committed, but its separately retryable execution did not. */
  approvalRecorded: boolean;
}

/**
 * Resolve through the EXISTING pending-action routes — the same path the Feed
 * uses, so FIT capture and trust events don't fork (spec §8).
 */
export function useResolveWorkItemPendingAction(): {
  resolveAction: (
    actionId: string,
    verb: ResolvePendingActionVerb,
    editedProposal?: Record<string, unknown> | null,
  ) => Promise<ResolvePendingActionResult>;
  isResolving: boolean;
  error: Error | null;
} {
  const [isResolving, setIsResolving] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const resolveAction = useCallback(
    async (
      actionId: string,
      verb: ResolvePendingActionVerb,
      editedProposal?: Record<string, unknown> | null,
    ) => {
      setIsResolving(true);
      setError(null);
      try {
        const body = verb === "approve" && editedProposal ? { editedProposal } : null;
        const res = await fetch(`/api/pending-actions/${actionId}/${verb}`, {
          method: "POST",
          credentials: "include",
          headers: body ? { "Content-Type": "application/json" } : undefined,
          body: body ? JSON.stringify(body) : undefined,
        });
        const responseText = await res.text();
        let responseBody: Record<string, unknown> | null = null;
        if (responseText) {
          try {
            const parsed = JSON.parse(responseText) as unknown;
            if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
              responseBody = parsed as Record<string, unknown>;
            }
          } catch {
            // A non-JSON error still falls back to the status-based message.
          }
        }
        const executionError = typeof responseBody?.executionError === "string"
          ? responseBody.executionError
          : null;
        const responseError = typeof responseBody?.error === "string"
          ? responseBody.error
          : null;
        if (executionError) {
          // The backend deliberately returns 502 after persisting approval so
          // the failed external effect remains retryable. Reflect that durable
          // state immediately instead of leaving the still-mounted card with a
          // stale "pending" approve button until its parent happens to reload.
          const wrapped = new Error(
            responseError ?? `Approval was recorded, but execution failed: ${executionError}`,
          );
          setError(wrapped);
          return { approvalRecorded: true };
        }
        if (!res.ok) {
          throw new Error(
            responseError
              ?? `${verb} failed (${res.status})`,
          );
        }
        return { approvalRecorded: false };
      } catch (err) {
        const wrapped = err instanceof Error ? err : new Error(`${verb} failed`);
        setError(wrapped);
        throw wrapped;
      } finally {
        setIsResolving(false);
      }
    },
    [],
  );

  return { resolveAction, isResolving, error };
}
